/**
 * versicherer-benachrichtigung.ts - P4-S7 (Phase D, steps 21-25): the vocab, wire DTOs and pure
 * helpers for the «Versicherer benachrichtigen» checkbox dialog over the customer's declared
 * insurances (F3 insurance_relationship), the per-carrier send of P-04.S22 with the signed
 * Analysemandat attached, and the start of the Fristueberwachung (customer-adjustable Frist).
 *
 * Nothing here touches the DB or NestJS: pure data + pure functions, shared verbatim by api
 * (the InsurerNotificationService send flow) and web (the dialog + panel + admin form).
 *
 * [Source: packages/shared/src/carrier-notification.ts] (const-object vocab + wire-DTO idiom, F1)
 * [Source: packages/shared/src/insurance-relationship.ts] (InsuranceRelationshipDto, the dialog rows)
 * [Source: docs/product/p4-p6-slices/p4-S7-versicherer-benachrichtigung-design.md] (authoritative shapes)
 */

import type { NotificationResolutionLevel } from './carrier-notification';
import type { InsuranceRelationshipDto, InsuranceRelationshipSource } from './insurance-relationship';
import { addArbeitstage } from './submission';
import { zurichDateOf, zurichEndOfDay } from './zurich-day';
import { LINE_OF_BUSINESS_LABELS } from './policy';
import type { MandateBadgeDto } from './mandate';
import type { RegistrationReminderErrorCode } from './mandate-registration';

/**
 * The lifecycle of one registration row (mandate x carrier): the analysis notification and the
 * Brokermandat registration share it. sending = the claim a send holds while its mail goes out;
 * zurueckgestellt / zur_freigabe = planned, no mail yet; benachrichtigt .. rueckfrage = the insurer's
 * answer is awaited; bestaetigt, abgelehnt, manuell_geschlossen = closed. The words a screen shows
 * come from registrationStatusLabel (mandate-registration.ts), never from a second map.
 */
export const MANDATE_INSURER_NOTIFICATION_STATUS = {
  SENDING: 'sending',
  ZURUECKGESTELLT: 'zurueckgestellt',
  ZUR_FREIGABE: 'zur_freigabe',
  BENACHRICHTIGT: 'benachrichtigt',
  ERINNERT: 'erinnert',
  KLAERUNG: 'klaerung',
  RUECKFRAGE: 'rueckfrage',
  BESTAETIGT: 'bestaetigt',
  ABGELEHNT: 'abgelehnt',
  MANUELL_GESCHLOSSEN: 'manuell_geschlossen',
  /** An analysis or Brokermandat row after its Auflösung was recorded (the insurer de-registered or was informed). */
  AUSGETRAGEN: 'ausgetragen',
  /** An information-only Auflösungsmeldung (an Analysemandat ended), closed when it is sent. */
  INFORMIERT: 'informiert',
} as const;
export type MandateInsurerNotificationStatus =
  (typeof MANDATE_INSURER_NOTIFICATION_STATUS)[keyof typeof MANDATE_INSURER_NOTIFICATION_STATUS];

/**
 * Statuses that count as OPEN: the double-send guard key + the overdue axis. Klärung (the reminder
 * loop's escalation) is still open: the insurer's slot stays taken (0190) and a broker can close it.
 */
export const OPEN_NOTIFICATION_STATUSES: readonly MandateInsurerNotificationStatus[] = [
  'benachrichtigt',
  'erinnert',
  'klaerung',
  'rueckfrage',
];

/**
 * Stefan 7.4 step 21 Quelle labels from the F3 source. 'portal' (P4-S3) is a customer-reported
 * declaration, so it carries the same «gemeldet» Quelle label as a manual broker entry.
 */
export const INSURANCE_RELATIONSHIP_SOURCE_LABEL: Record<
  InsuranceRelationshipSource,
  'erkannt' | 'gemeldet' | 'Bestand'
> = {
  extracted: 'erkannt',
  manual: 'gemeldet',
  bestand: 'Bestand',
  portal: 'gemeldet',
};

/** Stefan 17.5 defaults; the un-seeded values the config service surfaces (ist_default=true). */
export const INSURER_NOTIFICATION_FRIST_DEFAULTS = {
  frist_1_arbeitstage: 10,
  frist_2_arbeitstage: 5,
  erinnerungen: 1,
} as const;
export const INSURER_NOTIFICATION_FRIST_ARBEITSTAGE_MIN = 1;
export const INSURER_NOTIFICATION_FRIST_ARBEITSTAGE_MAX = 60;
export const INSURER_NOTIFICATION_ERINNERUNGEN_MIN = 0;
export const INSURER_NOTIFICATION_ERINNERUNGEN_MAX = 2;

/** The processes that reuse the notification table + Frist config (P4 Analyse, P5 Broker, P6). */
export const INSURER_NOTIFICATION_PROZESS_VALUES: readonly string[] = [
  'p4_analyse',
  'p5_broker',
  'p6_aufloesung',
];

/**
 * A Zurich working-day Frist: the Swiss calendar day of the send + n Arbeitstage (addArbeitstage,
 * Mon-Fri), ending at the end of that day in Europe/Zurich (23:59:59.999 local, DST-correct), so the
 * overdue comparison has the whole Swiss working day before it trips. A send at 00:30 in Zurich counts
 * from that day, not from the UTC day before. Pure.
 */
export function computeResponseDueAt(sentAtIso: string, fristArbeitstage: number): string {
  const dueDate = addArbeitstage(zurichDateOf(sentAtIso), fristArbeitstage);
  return zurichEndOfDay(dueDate);
}

/**
 * open status AND response_due_at strictly before now. Pure. Compares by INSTANT, not lexically:
 * response_due_at arrives from the DB as ::text (`2026-10-30 23:59:59.999+00`, space/+00) while
 * nowIso is an ISO string (`2026-10-30T...Z`, T/Z). A lexical `<` sorts the space (0x20) before
 * `T` (0x54), flipping to overdue at 00:00 on the due date. new Date(...) parses BOTH forms.
 */
export function isNotificationOverdue(
  row: { status: MandateInsurerNotificationStatus; response_due_at: string | null },
  nowIso: string,
): boolean {
  // Klärung is open, but its task is the open item, not the deadline (no «überfällig»).
  if (!OPEN_NOTIFICATION_STATUSES.includes(row.status) || row.status === 'klaerung' || row.response_due_at === null) return false;
  return new Date(row.response_due_at).getTime() < new Date(nowIso).getTime();
}

/** How one F3 row renders in the dialog (step 21 + 7.11 pre-selection). */
export type DialogRowState =
  | 'sendable' // exact / process_default / p5_inherited: preselected
  | 'sendable_fallback' // carrier_default: selectable, NOT preselected, «Hauptadresse» warning
  | 'no_address' // none: selectable, NOT preselected; ticking it creates the maintain task, no mail
  | 'unresolved_carrier' // carrier_uid NULL: not selectable (fix via F3 PATCH first)
  | 'already_notified'; // an OPEN notification covers this relationship: not selectable, status shown

/**
 * Classify one dialog row: its state, whether it is pre-ticked, and whether it is selectable.
 * An open notification wins over everything (7.11), then an unresolved carrier, then the F1 level.
 * Pure.
 */
export function classifyDialogRow(input: {
  carrierUid: string | null;
  level: NotificationResolutionLevel | null;
  openNotification: boolean;
  /** The relationship's F3 status: only a 'neu' row is still «nicht benachrichtigt» (A4) and pre-ticked. */
  relationshipStatus?: string;
}): { state: DialogRowState; preselected: boolean; selectable: boolean } {
  if (input.openNotification) {
    return { state: 'already_notified', preselected: false, selectable: false };
  }
  if (input.carrierUid === null) {
    return { state: 'unresolved_carrier', preselected: false, selectable: false };
  }
  switch (input.level) {
    case 'exact':
    case 'process_default':
    case 'p5_inherited':
      return {
        state: 'sendable',
        preselected: input.relationshipStatus === undefined || input.relationshipStatus === 'neu',
        selectable: true,
      };
    case 'carrier_default':
      return { state: 'sendable_fallback', preselected: false, selectable: true };
    case 'none':
    case null:
    default:
      return { state: 'no_address', preselected: false, selectable: true };
  }
}

/** The Sparte the dialog and the P-04.S22 mail show: the declared text, else the line-of-business label. Pure. */
export function sparteLabel(rel: Pick<InsuranceRelationshipDto, 'sparte_text' | 'line_of_business'>): string | null {
  return rel.sparte_text ?? (rel.line_of_business ? LINE_OF_BUSINESS_LABELS[rel.line_of_business] : null);
}

/** The P-04.S22 preview per resolvable carrier (7.11 «Vorschau»). */
export interface VersichererBenachrichtigungDraftDto {
  carrier_uid: string;
  subject: string;
  body_text: string;
}

/** One row of the dialog (step 21). */
export interface VersichererBenachrichtigungDialogRowDto {
  relationship: InsuranceRelationshipDto;
  source_label: 'erkannt' | 'gemeldet' | 'Bestand';
  carrier: { carrier_uid: string | null; name: string | null; versicherer_id: string | null };
  resolution: {
    email: string | null;
    level: NotificationResolutionLevel | null;
    address_id: string | null;
    maintenance_required: boolean;
  } | null; // null when carrier_uid is null
  state: DialogRowState;
  preselected: boolean;
  selectable: boolean;
  open_notification: {
    id: string;
    status: MandateInsurerNotificationStatus;
    /** The status in words (registrationStatusLabel). */
    status_label: string;
    sent_at: string | null;
    response_due_at: string | null;
    overdue: boolean;
  } | null;
}

/** GET /customers/:subjectType/:subjectId/versicherer-benachrichtigung/mandate: the dialog's Analysemandat, or null. */
export interface VersichererBenachrichtigungMandateDto {
  mandate_id: string | null;
}

/**
 * The mandate statuses the Versicherer-Benachrichtigung reads its Analysemandat from: the pending
 * released and sent ones (rows shown, send gated off) and the signed one, the only valid status.
 * draft (replaced by the next prepare), terminated and cancelled are no live mandate.
 */
export const BENACHRICHTIGUNG_MANDATE_STATUSES = ['released', 'sent', 'signed'] as const;
export type BenachrichtigungMandateStatus = (typeof BENACHRICHTIGUNG_MANDATE_STATUSES)[number];

/**
 * The Mandatskachel state each of those statuses shows, as the api's badge computes it for a live
 * mandate (released waits for its delivery: nicht erteilt; sent: in Verarbeitung; signed:
 * unterzeichnet). The dialog chip reads it with MANDATE_BADGE_STATE_LABEL; a badge spec pins the
 * two together.
 */
export const BENACHRICHTIGUNG_MANDATE_BADGE_STATE: Record<BenachrichtigungMandateStatus, MandateBadgeDto['state']> = {
  released: 'nicht_erteilt',
  sent: 'in_verarbeitung',
  signed: 'unterzeichnet',
};

/**
 * The live mandate of a kind for a subject ($1 tenant, $2 subject_type, $3 subject_id, $4 kind): one
 * of BENACHRICHTIGUNG_MANDATE_STATUSES, a signed one first, else the newest. Shared so the db tests
 * run the statement the api runs.
 */
export const BENACHRICHTIGUNG_MANDATE_SQL = `SELECT id,
       status,
       document_id    AS "documentId",
       process_number AS "processNumber"
  FROM mandate
 WHERE tenant_id = $1 AND subject_type = $2 AND subject_id = $3 AND kind = $4
   AND status IN (${BENACHRICHTIGUNG_MANDATE_STATUSES.map((s) => `'${s}'`).join(', ')})
 ORDER BY (status = 'signed') DESC, created_at DESC
 LIMIT 1`;

/** GET /customers/:subjectType/:subjectId/versicherer-benachrichtigung */
export interface VersichererBenachrichtigungDialogDto {
  mandate_id: string; // the live Analysemandat of the subject (released, sent or signed)
  /** released -> not_sent, sent -> not_signed, signed without a document -> no_document. */
  gate: { sendable: boolean; reason: 'ok' | 'not_sent' | 'not_signed' | 'no_document' };
  /** The mandate's status; the dialog chip shows it in the tile's words (BENACHRICHTIGUNG_MANDATE_BADGE_STATE). */
  mandate_status: BenachrichtigungMandateStatus;
  prozess: 'p4_analyse';
  kategorie: 'privat' | 'firmen';
  vorgangs_ref: string; // what the subject line will carry
  frist_arbeitstage: number; // the live Frist 1 (default 10)
  rows: VersichererBenachrichtigungDialogRowDto[];
  drafts: VersichererBenachrichtigungDraftDto[];
}

/**
 * The most relationship ids one send may carry: more ids at one insurer would exceed the btree key size
 * of the open-notification index. The api DTO validates it, the dialog enforces it before the send.
 */
export const VERSICHERER_BENACHRICHTIGUNG_MAX_RELATIONSHIPS = 150;

/** POST /mandates/:id/versicherer-benachrichtigung */
export interface SendVersichererBenachrichtigungBody {
  relationship_ids: string[]; // non-empty, uuids; THE human confirmation
  // Optional edited text per carrier: a non-blank body and the relationship ids it was written for,
  // which must be exactly the carrier's ticked rows.
  overrides?: Array<{ carrier_uid: string; body_text: string; relationship_ids: string[] }>;
}

export type SendCarrierOutcome = 'sent' | 'already_notified' | 'skipped_no_address' | 'failed';

/** The German label of each per-insurer send outcome. */
export const SEND_CARRIER_OUTCOME_LABEL: Record<SendCarrierOutcome, string> = {
  sent: 'Gesendet',
  already_notified: 'Bereits benachrichtigt',
  skipped_no_address: 'Keine Adresse, Aufgabe erstellt',
  failed: 'Fehlgeschlagen',
};

/** The German label of each prozess the Fristen are kept for. */
export const INSURER_NOTIFICATION_PROZESS_LABEL: Record<string, string> = {
  p4_analyse: 'P4 Analysemandat',
  p5_broker: 'P5 Brokermandat',
  p6_aufloesung: 'P6 Auflösung',
};

export interface SendVersichererBenachrichtigungCarrierResult {
  carrier_uid: string;
  outcome: SendCarrierOutcome;
  notification_id: string | null;
  task_id: string | null;
  resolution_level: NotificationResolutionLevel | null;
  relationship_ids: string[];
}

export interface SendVersichererBenachrichtigungResultDto {
  mandate_id: string;
  carriers: SendVersichererBenachrichtigungCarrierResult[];
  sent: number;
  already_notified: number;
  skipped_no_address: number;
  failed: number;
  fallback_carrier_uids: string[]; // sent via Hauptadresse (flagged)
}

/** GET /mandates/:id/versicherer-benachrichtigungen row (no e-mail body, no notes). */
export interface MandateInsurerNotificationDto {
  id: string;
  mandate_id: string;
  prozess: string;
  carrier_uid: string;
  carrier_name: string | null;
  /** NULL only on a planned Brokermandat registration (never on an analysis notification). */
  versicherer_id: string | null;
  relationship_ids: string[];
  recipient_email: string | null;
  resolution_level: NotificationResolutionLevel;
  template_ref: string;
  body_overridden: boolean;
  document_id: string;
  attachment_checksum: string | null;
  communication_id: string | null;
  vorgangs_ref: string;
  sent_at: string | null;
  sent_by_app_user_id: string | null;
  response_due_at: string | null;
  frist_arbeitstage: number;
  status: MandateInsurerNotificationStatus;
  /** The status in words (registrationStatusLabel, one word set for every process). */
  status_label: string;
  status_changed_at: string | null;
  reminded_at: string | null;
  /** The reminder loop (P4 step 24): reminders of the round, the deadline the last one set, the Klärung task. */
  reminded_count: number;
  frist_2_due_at: string | null;
  escalation_task_id: string | null;
  reminder_error_code: RegistrationReminderErrorCode | null;
  erinnerungen: number;
  closed_at: string | null;
  overdue: boolean; // computed on read
  /** «Jetzt erinnern» / «Eskalieren» the row allows (the caller's permissions are checked on top). */
  actions: { erinnern: boolean; eskalieren: boolean; versand_bestaetigen: boolean; nicht_versendet: boolean };
}

/** POST .../versicherer-benachrichtigungen/:notificationId/schliessen */
export interface SchliessenVersichererBenachrichtigungBody {
  notiz: string; // 1..2000 chars, required (Stefan: «schliesst mit Notiz»)
}

/** GET/PUT /admin/einstellungen/versicherer-fristen */
export interface InsurerNotificationFristConfigDto {
  prozess: string;
  frist_1_arbeitstage: number;
  frist_2_arbeitstage: number;
  erinnerungen: number;
  ist_default: boolean;
  geaendert_am: string | null;
}
export interface SetInsurerNotificationFristConfigBody {
  prozess: 'p4_analyse' | 'p5_broker' | 'p6_aufloesung';
  frist_1_arbeitstage: number;
  frist_2_arbeitstage: number;
  erinnerungen: number;
}

/** One carrier document row (step 23 Ablage read). */
export interface CarrierDocumentDto {
  document_id: string;
  filename: string;
  kind: string | null;
  type: string | null;
  created_at: string;
  linked_mandate_id: string | null;
}
export interface CarrierDocumentsResponse {
  items: CarrierDocumentDto[];
}
